import type { Dispatch } from "react"

import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  useAdvertiserIds,
  useObjectiveTypes,
} from "@/api/hooks/campaignForm"
import { AdvertiserAutocomplete } from "../widgets/AdvertiserAutocomplete"
import { CampaignChipBuilder } from "../widgets/CampaignChipBuilder"
import type { FormAction, FormState } from "../state/formReducer"

/**
 * Step 1 — Account, objective, campaign names, and campaign toggles.
 */

interface Props {
  state: FormState
  dispatch: Dispatch<FormAction>
  campaignError?: string
}

export function AccountStep({ state, dispatch, campaignError }: Props) {
  const query = useAdvertiserIds()
  const objectivesQuery = useObjectiveTypes(true)
  const objectives = objectivesQuery.data ?? []

  return (
    <section className="rounded-lg border border-[#4a4a4a] bg-[#2d2d2d] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]">
      <h3 className="mb-3 text-lg font-semibold">
        📱 Step 1: Account &amp; Campaign Settings
      </h3>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-4">
        <div className="min-w-0 flex-1">
          <AdvertiserAutocomplete
            advertisers={query.data}
            isLoading={query.isLoading}
            isError={query.isError}
            value={state.advertiserId}
            onSelect={(id, label) =>
              dispatch({ type: "SET_ADVERTISER", id, label })
            }
          />
        </div>
        <div className="min-w-0 w-full sm:w-64 sm:shrink-0">
          <Label>Campaign Objective:</Label>
          <div className="relative">
            <input
              tabIndex={-1}
              aria-hidden="true"
              required
              value={state.objectiveType ?? ""}
              onChange={() => {}}
              className="pointer-events-none absolute inset-0 h-full w-full opacity-0"
            />
            <Select
              value={state.objectiveType ?? ""}
              onValueChange={(value) =>
                dispatch({ type: "SET_OBJECTIVE", value: value || null })
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select objective..." />
              </SelectTrigger>
              <SelectContent>
                {objectives.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {state.advertiserId ? (
        <>
          <div className="mt-4">
            <Label>Campaign Name:</Label>
            <CampaignChipBuilder
              campaigns={state.campaigns}
              input={state.campaignNameInput}
              error={campaignError}
              onInputChange={(value) =>
                dispatch({ type: "SET_CAMPAIGN_NAME_INPUT", value })
              }
              onAdd={() =>
                dispatch({
                  type: "ADD_CAMPAIGN",
                  name: state.campaignNameInput,
                })
              }
              onRemove={(id) => dispatch({ type: "REMOVE_CAMPAIGN", id })}
            />
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2">
            <label className="flex items-center gap-2">
              <Checkbox
                checked={state.isSmartPlusCampaign}
                onCheckedChange={(value) =>
                  dispatch({ type: "SET_SMART_PLUS", value: value === true })
                }
              />
              <span className="text-sm font-semibold">Smart+ Campaign</span>
            </label>
            <label className="flex items-center gap-2">
              <Checkbox
                checked={state.isCboCampaign}
                onCheckedChange={(value) =>
                  dispatch({ type: "SET_CBO", value: value === true })
                }
              />
              <span className="text-sm font-semibold">
                Campaign Budget Optimization (CBO)
              </span>
            </label>
            <label className="flex items-center gap-2">
              <Checkbox
                checked={state.skipLearningPhase}
                onCheckedChange={(value) =>
                  dispatch({
                    type: "SET_SKIP_LEARNING_PHASE",
                    value: value === true,
                  })
                }
              />
              <span className="text-sm font-semibold">Skip Learning Phase</span>
            </label>
          </div>
        </>
      ) : null}
    </section>
  )
}
